// DD Consulting — Insights source loader
// ------------------------------------------------------------------
// Resilient, user-editable data source for the landing-page AI
// insights banner and the three tier cards (CIO / CTO / SME).
//
// Flow:
//   1. On mount, the UI calls `loadInsights()` which:
//      a. Reads localStorage cache `dd_insights_cache_v1`. If fresh
//         (< 1 hour old), returns it immediately.
//      b. Else, if REACT_APP_INSIGHTS_SHEET_URL is set, fetches the
//         published Google Sheet CSV (4-second AbortController timeout).
//      c. Parses the CSV into { banner: [], tiers: { CIO, CTO, SME } }.
//      d. Validates: must have ≥1 banner AND ≥1 entry for each tier.
//      e. Merges into the bundled seed (sheet entries win; seed is the
//         floor so a sparse sheet never produces an empty pool).
//      f. Writes { fetchedAt, data } back to localStorage.
//   2. If ANY step fails (network, CORS, timeout, parse, validation),
//      the bundled seed is returned. The UI never sees "offline".
//
// Matching is case-insensitive on the tier column. Accepted tier
// values in the sheet: banner | BANNER | cio | CIO | cto | CTO |
// sme | SME. Everything else is ignored silently.

import { INSIGHTS_SEED } from "@/constants/insightsSeed";

const CACHE_KEY = "dd_insights_cache_v1";
const TTL_MS = 60 * 60 * 1000; // 1 hour
const FETCH_TIMEOUT_MS = 4000;

// Public published-to-web sheet URL. Hardcoded as a runtime fallback so
// CI/CD builds without a `.env` file (it's gitignored) still ship working
// data fetching. Override locally via REACT_APP_INSIGHTS_SHEET_URL.
const DEFAULT_INSIGHTS_SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1Z_ovzL7cyTGLYTTKk9Fyl47B1SHtmvQBN-1Quiqm7oM/export?format=csv";

// Tier keys the UI understands. "banner" is a pseudo-tier for the
// passive banner at the top of the Value Architecture section.
const KNOWN_TIERS = ["CIO", "CTO", "SME"];
const ALL_KNOWN_KEYS = ["banner", ...KNOWN_TIERS];

// Normalize any incoming tier string to one of the known keys, or null.
function normalizeTierKey(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // Banner (case-insensitive)
  if (s.toLowerCase() === "banner") return "banner";
  // CIO / CTO / SME (case-insensitive)
  const upper = s.toUpperCase();
  if (KNOWN_TIERS.includes(upper)) return upper;
  return null;
}

// Build an empty pool with all expected keys so downstream code can
// safely read pool.banner / pool.tiers[key] without optional chaining.
function emptyPool() {
  return {
    banner: [],
    tiers: { CIO: [], CTO: [], SME: [] },
  };
}

// Deep-clone the seed so callers can't accidentally mutate the module.
function cloneSeed() {
  return {
    banner: [...INSIGHTS_SEED.banner],
    tiers: {
      CIO: [...INSIGHTS_SEED.tiers.CIO],
      CTO: [...INSIGHTS_SEED.tiers.CTO],
      SME: [...INSIGHTS_SEED.tiers.SME],
    },
  };
}

// ------------------------------------------------------------------
// Tiny RFC-4180-ish CSV parser.
// Handles:
//   - Quoted fields that contain commas
//   - Quoted fields with escaped quotes (`""`)
//   - CRLF and LF line terminators
//   - Trailing empty lines
// Returns a 2D array of strings.
function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        // Escaped quote?
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }

    if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\r") {
      // Normalize CRLF — skip and let \n handle it
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }

  // Flush last field / row if file doesn't end with newline
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Strip trailing fully-empty rows
  while (rows.length && rows[rows.length - 1].every((v) => v === "")) {
    rows.pop();
  }
  return rows;
}

// Convert a 2D CSV array into a validated pool.
//
// Sheet-as-source-of-truth semantics (per-key override):
//   - For each tier key (banner / CIO / CTO / SME), if the sheet has
//     ≥1 entry, the sheet REPLACES the seed for that key entirely.
//   - If the sheet has NO entries for a key, the seed entries for that
//     key are kept as a graceful fallback so the UI never empties.
//
// This is what a non-technical CMS user expects: "what I put in the
// sheet is what shows on the site."
function rowsToPool(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  // First row is treated as header; we try to detect columns by name
  // but fall back to positional (col 0 = tier, col 1 = text).
  let header = rows[0].map((h) => String(h || "").trim().toLowerCase());
  let tierIdx = header.indexOf("tier");
  let textIdx = header.indexOf("text");
  let bodyStart = 1;

  // If no header detected, assume positional
  if (tierIdx === -1 || textIdx === -1) {
    tierIdx = 0;
    textIdx = 1;
    bodyStart = 0;
  }

  // Collect sheet entries into a fresh, empty pool first.
  const sheetPool = emptyPool();

  for (let r = bodyStart; r < rows.length; r++) {
    const row = rows[r];
    if (!row || row.length === 0) continue;
    const tier = normalizeTierKey(row[tierIdx]);
    const text = row[textIdx] ? String(row[textIdx]).trim() : "";
    if (!tier || !text) continue;

    if (tier === "banner") {
      sheetPool.banner.push(text);
    } else {
      sheetPool.tiers[tier].push(text);
    }
  }

  // Build the final pool: sheet REPLACES seed per-key when present;
  // seed is the fallback only for keys the sheet didn't populate.
  const seed = cloneSeed();
  const finalPool = {
    banner: sheetPool.banner.length > 0 ? sheetPool.banner : seed.banner,
    tiers: {
      CIO: sheetPool.tiers.CIO.length > 0 ? sheetPool.tiers.CIO : seed.tiers.CIO,
      CTO: sheetPool.tiers.CTO.length > 0 ? sheetPool.tiers.CTO : seed.tiers.CTO,
      SME: sheetPool.tiers.SME.length > 0 ? sheetPool.tiers.SME : seed.tiers.SME,
    },
  };

  // Validate: must have ≥1 entry for every known key. Seed guarantees
  // this, but we check defensively in case the seed is ever emptied.
  if (finalPool.banner.length === 0) return null;
  for (const t of KNOWN_TIERS) {
    if (!finalPool.tiers[t] || finalPool.tiers[t].length === 0) return null;
  }

  return finalPool;
}

// ------------------------------------------------------------------
// localStorage cache helpers (guard against SSR / private mode)
function readCache() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.fetchedAt !== "number" || !parsed.data) return null;
    if (Date.now() - parsed.fetchedAt > TTL_MS) return null;
    return parsed.data;
  } catch (_err) {
    return null;
  }
}

function writeCache(data) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ fetchedAt: Date.now(), data })
    );
  } catch (_err) {
    // Quota exceeded / disabled — silent, seed still works.
  }
}

// ------------------------------------------------------------------
// Public API

// Synchronous: immediate seed pool for first paint.
export function getSeedPool() {
  return cloneSeed();
}

// Pick a random insight string from a pool for the given key.
// key = "banner" | "CIO" | "CTO" | "SME"
export function pickInsight(pool, key) {
  if (!pool) return "";
  const arr = key === "banner" ? pool.banner : (pool.tiers && pool.tiers[key]) || [];
  if (!arr || arr.length === 0) return "";
  return arr[Math.floor(Math.random() * arr.length)];
}

// Asynchronous loader. Never rejects — always resolves to a valid pool.
export async function loadInsights() {
  // 1. Fresh cache?
  const cached = readCache();
  if (cached) return cached;

  // 2. Sheet URL configured?
  // NOTE: In CRA, `process.env.REACT_APP_*` is replaced at build time by
  // webpack's DefinePlugin with the literal string value. We must NOT
  // guard it with `typeof process !== "undefined"` because that prevents
  // the substitution and the expression short-circuits to "" in the
  // browser (where `process` really is undefined at runtime).
  const url = process.env.REACT_APP_INSIGHTS_SHEET_URL || DEFAULT_INSIGHTS_SHEET_URL;

  if (!url) {
    // No sheet configured AND no compiled-in default — seed is the
    // source of truth. Don't cache it (so if the user later sets the
    // env var, the next load fetches).
    return cloneSeed();
  }

  // 3. Try to fetch + parse.
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const res = await fetch(url, {
      method: "GET",
      signal: controller.signal,
      // Sheet pub-to-web endpoints are CORS-open (GET, *), so no mode gymnastics.
      cache: "no-store",
      credentials: "omit",
    });
    clearTimeout(timer);

    if (!res.ok) throw new Error(`sheet fetch returned HTTP ${res.status}`);

    // Guard against HTML-error-page content type
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (!ct.includes("csv") && !ct.includes("text/plain") && !ct.includes("application/octet-stream")) {
      // Still try to parse — some CDNs return text/html for CSVs; we only
      // fail if parsing fails. But log for diagnosis.
      // eslint-disable-next-line no-console
      console.warn(`[DD Insights] unexpected content-type "${ct}" — attempting parse anyway`);
    }

    const text = await res.text();
    const rows = parseCSV(text);
    const pool = rowsToPool(rows);
    if (!pool) throw new Error("sheet parsed but did not satisfy validation (missing banner or tier)");

    writeCache(pool);
    return pool;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[DD Insights] sheet load failed — using bundled seed:", err?.message || err);
    return cloneSeed();
  }
}

// Expose constants for tests / debugging
export const __INTERNAL__ = {
  CACHE_KEY,
  TTL_MS,
  FETCH_TIMEOUT_MS,
  KNOWN_TIERS,
  ALL_KNOWN_KEYS,
  parseCSV,
  rowsToPool,
  normalizeTierKey,
};
